// src/pages/employer/profile/EmployerTeams.jsx
// Profile-level teams. Admins (org OWNER or profile ADMIN) can create teams,
// rename or delete them, and add/remove profile members from each team.

import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import { orgAPI, profileTeamAPI } from '../../../services/api';
import { Modal, useConfirm } from '../../../components/shared/UI';
import { Plus, Pencil, Trash2, X, UserPlus, Users } from 'lucide-react';

const fullName = (jsp) => {
  const f = jsp?.firstName || '';
  const l = jsp?.lastName  || '';
  const name = `${f} ${l}`.trim();
  return name || null;
};

const memberLabel = (m) => {
  const u = m?.groupProfileMember?.user;
  return fullName(u?.jobSeekerProfile)
    || u?.email
    || m?.groupProfileMember?.email
    || 'Unknown';
};

const memberSubLabel = (m) => {
  const u = m?.groupProfileMember?.user;
  return u?.email || m?.groupProfileMember?.email || '';
};

const profileMemberLabel = (pm) => {
  return fullName(pm.user?.jobSeekerProfile)
    || pm.user?.email
    || pm.email
    || 'Unknown';
};

const TeamModal = ({ open, onClose, onSave, initial, saving }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  useEffect(() => {
    if (open) {
      setName(initial?.name || '');
      setDescription(initial?.description || '');
    }
  }, [open, initial]);
  const canSave = name.trim().length > 0 && !saving;
  return (
    <Modal open={open} onClose={onClose}
      title={initial ? 'Edit team' : 'New team'} width="460px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSave}
            onClick={() => onSave({ name: name.trim(), description: description.trim() || null })}>
            {saving ? 'Saving…' : (initial ? 'Save' : 'Create team')}
          </button>
        </div>
      }>
      <label className="form-label">Team name</label>
      <input className="form-input" autoFocus value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Hiring panel — Engineering" />
      <label className="form-label" style={{ marginTop: 12 }}>Description (optional)</label>
      <textarea className="form-input" rows={3} value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What is this team responsible for?" />
    </Modal>
  );
};

const AddMemberPicker = ({ candidates, onAdd, busy }) => {
  const [selected, setSelected] = useState('');
  if (candidates.length === 0) {
    return (
      <p style={{ fontSize: 12, color: '#888', fontStyle: 'italic', margin: 0 }}>
        Everyone on this profile is already in this team.
      </p>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <select value={selected} onChange={(e) => setSelected(e.target.value)} disabled={busy}
        style={{ flex: 1, fontSize: 13, padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4 }}>
        <option value="">— Pick a profile member —</option>
        {candidates.map(pm => (
          <option key={pm.id} value={pm.id}>
            {profileMemberLabel(pm)}
            {pm.inviteStatus !== 'ACCEPTED' ? ' (pending)' : ''}
          </option>
        ))}
      </select>
      <button className="btn btn-primary btn-sm" disabled={!selected || busy}
        onClick={() => { onAdd(selected); setSelected(''); }}>
        <UserPlus size={13} style={{ marginRight: 4 }} /> Add
      </button>
    </div>
  );
};

const TeamCard = ({ team, profileMembers, onEdit, onDelete, onAddMember, onRemoveMember }) => {
  const [busy, setBusy] = useState(false);
  const memberIds = new Set(team.members.map(m => m.groupProfileMemberId));
  const candidates = profileMembers.filter(pm => !memberIds.has(pm.id));

  const handleAdd = async (groupProfileMemberId) => {
    setBusy(true);
    try { await onAddMember(team.id, groupProfileMemberId); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 14, overflow: 'hidden' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px', background: '#0e4e95', color: '#fff',
      }}>
        <Users size={16} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{team.name}</div>
          {team.description && <div style={{ fontSize: 12, opacity: 0.85 }}>{team.description}</div>}
        </div>
        <span style={{ fontSize: 11, opacity: 0.85 }}>
          {team.members.length} member{team.members.length !== 1 ? 's' : ''}
        </span>
        <button onClick={() => onEdit(team)} title="Edit team"
          style={{ background: 'transparent', color: '#fff', border: 'none', cursor: 'pointer', padding: 4 }}>
          <Pencil size={14} />
        </button>
        <button onClick={() => onDelete(team)} title="Delete team"
          style={{ background: 'transparent', color: '#fff', border: 'none', cursor: 'pointer', padding: 4 }}>
          <Trash2 size={14} />
        </button>
      </div>

      <div style={{ padding: 14 }}>
        {team.members.length === 0 ? (
          <p style={{ fontSize: 13, color: '#999', fontStyle: 'italic', margin: '0 0 12px' }}>
            No members yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {team.members.map(m => (
              <div key={m.id} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 4px 4px 10px', borderRadius: 18,
                background: '#f0f6ff', border: '1px solid #d6e6f7', fontSize: 12,
              }}>
                <span style={{ fontWeight: 600, color: '#0e4e95' }}>{memberLabel(m)}</span>
                {memberSubLabel(m) && memberSubLabel(m) !== memberLabel(m) && (
                  <span style={{ color: '#888' }}>· {memberSubLabel(m)}</span>
                )}
                <button onClick={() => onRemoveMember(team.id, m.groupProfileMemberId)}
                  title="Remove from team"
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    padding: 2, marginLeft: 2, color: '#3D80CC',
                  }}>
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
        <AddMemberPicker candidates={candidates} onAdd={handleAdd} busy={busy} />
      </div>
    </div>
  );
};

export const EmployerTeams = () => {
  const { activeOrg, activeProfile, isAdmin } = useAuth();
  const { confirm, dialog } = useConfirm();

  const [loading, setLoading]         = useState(true);
  const [teams, setTeams]             = useState([]);
  const [profileMembers, setProfileMembers] = useState([]);

  const [teamModal, setTeamModal]     = useState(null);   // null | { initial?: team }
  const [savingTeam, setSavingTeam]   = useState(false);

  const reload = async () => {
    if (!activeOrg || !activeProfile) return;
    setLoading(true);
    try {
      const [teamsRes, membersRes] = await Promise.all([
        profileTeamAPI.list(activeOrg.id, activeProfile.id),
        orgAPI.getProfileMembers(activeOrg.id, activeProfile.id),
      ]);
      setTeams(teamsRes.data?.data?.teams || []);
      setProfileMembers(membersRes.data?.data?.members || []);
    } catch {
      // interceptor
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeOrg?.id, activeProfile?.id]);

  const handleSaveTeam = async ({ name, description }) => {
    setSavingTeam(true);
    try {
      if (teamModal?.initial) {
        await profileTeamAPI.update(activeOrg.id, activeProfile.id, teamModal.initial.id, { name, description });
        toast.success('Team updated');
      } else {
        await profileTeamAPI.create(activeOrg.id, activeProfile.id, { name, description });
        toast.success('Team created');
      }
      setTeamModal(null);
      await reload();
    } catch {
      // interceptor
    } finally {
      setSavingTeam(false);
    }
  };

  const handleDeleteTeam = async (team) => {
    if (!await confirm('Delete team', `"${team.name}" will be permanently removed. Members stay on the profile.`)) return;
    try {
      await profileTeamAPI.delete(activeOrg.id, activeProfile.id, team.id);
      toast.success('Team deleted');
      await reload();
    } catch {
      // interceptor
    }
  };

  const handleAddMember = async (teamId, groupProfileMemberId) => {
    try {
      const { data } = await profileTeamAPI.addMember(activeOrg.id, activeProfile.id, teamId, { groupProfileMemberId });
      const fresh = data?.data?.team;
      if (fresh) setTeams((prev) => prev.map(t => t.id === teamId ? fresh : t));
    } catch {
      // interceptor
    }
  };

  const handleRemoveMember = async (teamId, groupProfileMemberId) => {
    try {
      const { data } = await profileTeamAPI.removeMember(activeOrg.id, activeProfile.id, teamId, groupProfileMemberId);
      const fresh = data?.data?.team;
      if (fresh) setTeams((prev) => prev.map(t => t.id === teamId ? fresh : t));
    } catch {
      // interceptor
    }
  };

  const acceptedAndPendingMembers = useMemo(
    () => profileMembers.filter(pm => pm.inviteStatus === 'ACCEPTED' || pm.inviteStatus === 'PENDING'),
    [profileMembers],
  );

  return (
    <div className="fade-in">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0 }}>Teams</h1>
          <p style={{ margin: '2px 0 0', color: 'var(--text-muted)', fontSize: 13 }}>
            Group profile members into teams for shared ownership and assignment.
          </p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => setTeamModal({})}>
            <Plus size={13} style={{ marginRight: 4 }} /> New team
          </button>
        )}
      </div>

      {loading ? (
        <p style={{ color: '#888', padding: 20 }}>Loading…</p>
      ) : teams.length === 0 ? (
        <div style={{
          background: 'white', border: '1px dashed var(--border)', borderRadius: 6,
          padding: 32, textAlign: 'center', color: '#888', marginTop: 16,
        }}>
          <Users size={28} style={{ marginBottom: 8, opacity: 0.6 }} />
          <div style={{ fontSize: 14, marginBottom: 8 }}>No teams yet.</div>
          {isAdmin && (
            <button className="btn btn-primary btn-sm" onClick={() => setTeamModal({})}>
              <Plus size={13} style={{ marginRight: 4 }} /> Create your first team
            </button>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 16 }}>
          {teams.map(team => (
            <TeamCard key={team.id} team={team}
              profileMembers={acceptedAndPendingMembers}
              onEdit={(t) => setTeamModal({ initial: t })}
              onDelete={handleDeleteTeam}
              onAddMember={handleAddMember}
              onRemoveMember={handleRemoveMember} />
          ))}
        </div>
      )}

      <TeamModal open={!!teamModal} onClose={() => setTeamModal(null)}
        initial={teamModal?.initial} saving={savingTeam} onSave={handleSaveTeam} />
      {dialog}
    </div>
  );
};
